import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { styled } from "@pigment-css/react";

// Height-animated container. Children stay mounted (so collapsing animates too) and are measured
// via a ResizeObserver so the open height tracks content reflow (locale/font/text changes). This
// is the same technique as CreateTodoSheet's ExpandableAccordion, minus the calendar-specific
// padding and scroll-pinning — the recap edit screens are full-screen, not a height-capped sheet.
const Outer = styled("div")(({ theme }) => ({
  overflow: "hidden",
  transition: `height ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}, opacity ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.standard}`,
}));

interface CollapseProps {
  open: boolean;
  children: ReactNode;
}

export function Collapse({ open, children }: CollapseProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    // Round up so a sub-pixel content height never clips the trailing border/content.
    const measure = () => setHeight(Math.ceil(el.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Outer style={{ height: open ? height : 0, opacity: open ? 1 : 0 }}>
      <div ref={innerRef}>{children}</div>
    </Outer>
  );
}
